import moment from "moment-timezone";
import { useCheckAuthorityType, useCheckUser } from '../../../hooks/useCheckUser';
import { usePricing } from '../../../hooks/usePricing';
import { useFilterState } from '../../cap';
import { useAtp } from '../../cap/hooks/useATP';
import { updateEstimates } from '../../../aem-core-components/actions/cart';
import { useUserContext } from '../../../aem-core-components/context/UserContext';
import { useCartState } from '../../../contexts/cart/cartContext';
import { getInventoryPcs } from '../../cap/utils/atputils';
import { convertDateToMomentDateTime, formatjobsiteZip, generateSKU, isAlternateCID } from '../../global/utils/commonUtils';
import { getDistance } from '../../cart/utils/helper';
import { isValidString, logError } from '../../global/utils/logger';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import {
    RESET_GEOGRAPHY,
    RESET_ORDER_ESTIMATES,
    SET_IS_CART_LOADING,
    SET_LOADERS_FIELD,
    SET_SAVED_AVAILABLE_QUANTITY
} from '../../../aem-core-components/actions/constants';
import { USER_TYPE } from '../../../constants/userDetailsConstants';
import { TILE_STATES } from '../../cap/constants';
import { AUTHORITY_TYPE, FORCE_ITEM } from '../../global/constants';
import { LOADERS } from '../../../constants/cartConstants';
import { LINE_ITEM_TYPE_MISC, LINE_ITEM_TYPE_SALES } from "../../molecules/orderSummaryFactory/constants/orderSummaryFactoryConstants";

const useCartEstimate = () => {
    const userType = useCheckUser();
    const [{ cart, cartId, howToGetYourOrderDetails, userAccount, consumables }, dispatch] =
        useCartState();
    const [{ viewCart, projectDetails, startDate, endDate }, filterDispatch] = useFilterState();
    const [{ userProfile, isUserProfileLoading }, { getProjectsFromCookie }] = useUserContext();
    const { pc, location, lat, long, jobSiteAddr2, jobSiteCity, jobSiteState, jobSiteZip, isInStorePickup } = viewCart;
    const { findNearestPc, getOverrideRatesPc } = usePricing();
    const { handleATPCart } = useAtp();
    const authorityType = useCheckAuthorityType();
    const isP2PUser = authorityType === AUTHORITY_TYPE.P2P;

    const isSourcePCCallCriteria = (updatedItems = cart?.items) => {
        let sourcePCObj = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SOURCEPCOBJ)
            ? JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SOURCEPCOBJ))
            : {};
        let callPC = false;
        /* logic for triggering the PC call when dates or quantity changes */
        if (updatedItems?.length > 0 && startDate && endDate) {
            callPC = true;
        } else if (
            /* logic for trigerring the PC call on page load if sourcePC is not there in sessionStorage so as to avoid source call on page load */
            !isUserProfileLoading &&
            startDate &&
            endDate &&
            isValidString(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY)) > 0 &&
            !parseInt(sourcePCObj?.pc)
        ) {
            callPC = true;
        }
        return callPC;
    };

    const isEstimatesWithOwnedPc = (updatedItems = cart?.items) => {
        if (isSourcePCCallCriteria(updatedItems) || getOverrideRatesPc()) {
            return false;
        } else {
            return true;
        }
    };

    const formatSelectedAccessories = (selectedConsumables = consumables?.selected) => {
        try {
            const selectedAccessories = {};
            for (let i in selectedConsumables) {
                const rentals = selectedConsumables?.[i]?.rentals || {};
                for (const j in rentals) {
                    if (rentals?.[j]?.isAvailable) {
                        if (selectedAccessories[j]?.quantity > 0) {
                            // updating qty if same accessory is added multiple times
                            selectedAccessories[j].quantity += rentals?.[j]?.qty;
                        } else {
                            selectedAccessories[j] = {
                                catId: ('000' + j?.slice(0, 3))?.slice(-3),
                                classId: ('0000' + j?.slice(3, 7))?.slice(-4),
                                quantity: rentals?.[j]?.qty,
                                isItemAvailable: true // will always be true for accessories
                            };
                        }
                    }
                }
            }
            return selectedAccessories;
        } catch (error) {
            logError(error, false, 'formatSelectedAccessories', [selectedConsumables]);
            return {};
        }
    }

    const getEquipmentData = (updatedItems = []) => {
        let items = updatedItems?.length > 0 ? updatedItems : cart?.items;
        let equipments = [];
        const nearbyPc = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.CID_PC_LIST));

        items?.forEach(item => {
            const { sku = '' } = item?.product || {};
            const quantity = item?.quantity;
            const catId = ('000' + sku.slice(0, 3)).slice(-3);
            const classId = ('0000' + sku.slice(3, 7)).slice(-4);
            let tileStatus = handleATPCart({
                catsku: sku,
                inventoryDataObj: item?.product?.ec_pc_inventory
            });
            if (isEstimatesWithOwnedPc(items)) {
                equipments.push({
                    catId,
                    classId,
                    quantity,
                    ownedPc: findNearestPc(getInventoryPcs(item?.product?.ec_pc_inventory), nearbyPc),
                    isItemAvailable: tileStatus === TILE_STATES.AVAILABLE || tileStatus === TILE_STATES.AVAILABLE_WITH_WARNING
                });
            } else {
                equipments.push({
                    catId,
                    classId,
                    quantity,
                    isItemAvailable: tileStatus === TILE_STATES.AVAILABLE || tileStatus === TILE_STATES.AVAILABLE_WITH_WARNING
                });
            }
        });

        return equipments;
    };

    const getSalesEquipmentData = (selectedConsumables = consumables?.selected) => {
        let newArray = [];
        Object.keys(selectedConsumables).forEach(item => {
            let selectedArray = Object.keys(selectedConsumables[item]);
            if (selectedArray.includes('addons')) {
                let purchaseArray = Object.values(selectedConsumables[item]['addons']);
                purchaseArray.forEach(itemAddon => {
                    const lineItemType = itemAddon?.itemObj?.lineItemType?.toUpperCase();
                    const isForcedAddons = itemAddon?.itemObj?.forceItem == FORCE_ITEM.YES && [LINE_ITEM_TYPE_SALES, LINE_ITEM_TYPE_MISC].includes(lineItemType);
                    if (itemAddon?.isAvailable && !isForcedAddons) { // excluding item in salesEquipments in estimate request if forced item = Y  AND  Lineitemtype= misc OR sales according to the comment https://sunbelt.atlassian.net/browse/ECOM-22129?focusedCommentId=559579 given by Priyanka Narale
                        newArray.push({
                            itemNumber: itemAddon?.itemObj?.itemNumber,
                            quantity: itemAddon?.qty,
                            unitPrice: itemAddon?.price,
                            stockClass: itemAddon?.itemObj?.stockClass
                        });
                    }
                });
            }
        });
        return newArray;
    };

    const getStartDate = (isFromHTGO = false) => {
        let pickupTime = '';
        // TODO: @Nikhil split the code for CAP and checkout.
        if (!isInStorePickup) {
            let startDateTimeLocale;
            let startDateLocale = startDate;
            let startTimeLocale = '';
            if (howToGetYourOrderDetails.selectedDeliveryTime?.label) {
                startDateTimeLocale = howToGetYourOrderDetails.selectedDeliveryTime.slot?.split(' - ')[1];
                startTimeLocale = startDateTimeLocale.split('T')[1];
            } else if (howToGetYourOrderDetails.selectedPickUpTime?.label) {
                //if there is no delivery time, take time from pickup time for estimates call, date remains the same
                startDateTimeLocale = howToGetYourOrderDetails.selectedPickUpTime.slot?.split(' - ')[0]; // taking start time = pickup time, but date is the start date
                startTimeLocale = startDateTimeLocale.split('T')[1];
            }
            return convertDateToMomentDateTime(startDateLocale, startTimeLocale);
        } else {
            if (howToGetYourOrderDetails.selectedPickupTime) {
                pickupTime = moment(howToGetYourOrderDetails.selectedPickupTime, 'h:mm A');
            } else if (howToGetYourOrderDetails.selectedReturnTime) {
                pickupTime = moment(howToGetYourOrderDetails.selectedReturnTime, 'h:mm A');
            }
            return convertDateToMomentDateTime(
                isFromHTGO ? howToGetYourOrderDetails.selectedStartDate : startDate,
                pickupTime
            );
        }
    };

    const getEndDate = (isFromHTGO = false, localEndDate = '') => {
        let returnTime = '';
        // TODO: @Nikhil split the code for CAP and checkout.
        if (!isInStorePickup) {
            let endDateTimeLocale;
            let endDateLocale = endDate;
            let endTimeLocale = '';
            if (howToGetYourOrderDetails.selectedPickUpTime?.label) {
                endDateTimeLocale = howToGetYourOrderDetails.selectedPickUpTime.slot?.split(' - ')[0];
                endTimeLocale = endDateTimeLocale.split('T')[1];
            } else if (howToGetYourOrderDetails.selectedDeliveryTime?.label) {
                //if there is no pickup time, take time from delivery time for estimates call, date remains the same
                endDateTimeLocale = howToGetYourOrderDetails.selectedDeliveryTime.slot?.split(' - ')[1]; // taking end time = delivery time, but date is the end date
                endTimeLocale = endDateTimeLocale.split('T')[1];
            }
            return convertDateToMomentDateTime(endDateLocale, endTimeLocale);
        } else {
            if (howToGetYourOrderDetails.selectedReturnTime) {
                returnTime = moment(howToGetYourOrderDetails.selectedReturnTime, 'h:mm A');
            } else if (howToGetYourOrderDetails.selectedPickupTime) {
                returnTime = moment(howToGetYourOrderDetails.selectedPickupTime, 'h:mm A');
            }
            return convertDateToMomentDateTime(
                isFromHTGO ? howToGetYourOrderDetails.selectedEndDate : endDate,
                returnTime
            );
        }
    };

    const getTimeType = () => {
        const delivery = 'Delivery';
        const pickup = 'Pickup';

        if (viewCart?.isInStorePickup) {
            return pickup;
        }
        return delivery;
    };

    const updateAccessoriesPrice = (cartEstimates, accessories, rentablesPriceObj = consumables?.rentablesWithPrice) => {
        try {
            const rentalPriceObj = { ...(rentablesPriceObj || {}) };
            cartEstimates?.estimatesResponse?.estimate?.itemizedCharges?.products?.forEach(product => {
                const productSku = generateSKU(product?.catId, product?.classId);
                if (accessories[productSku]) {
                    rentalPriceObj[productSku] = product?.unitRentalCost;
                }
            });
            dispatch({ type: SET_SAVED_AVAILABLE_QUANTITY, key: 'rentablesWithPrice', value: rentalPriceObj });
            return rentalPriceObj;
        } catch (error) {
            logError(error, false, 'updateAccessoriesPrice', [accessories]);
            return {};
        }
    };

    const getEstimates = async (
        localLocation,
        localPc = 0,
        localPCLat = '',
        localPCLong = '',
        isFromHTGO = false,
        updatedItems = [],
        signal,
        cartResponse = {},
        consumablesData = {}
    ) => {
        try {
            dispatch({
                type: SET_LOADERS_FIELD,
                key: LOADERS.IS_ESTIMATES_LOADING,
                value: true
            });
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.IS_ESTIMATES_LOADING, true);
            const projectCookies = getProjectsFromCookie();
            let payload = {};
            let browsePCObj = null;
            let cartEstimates = null;
            let commonPayload = {
                cartId,
                distance: getDistance(viewCart?.isInStorePickup, true),
                timeType: getTimeType(),
                lastResort: false,
                fulfillment: 1,
                dlSt: userType === USER_TYPE.CASH ? userProfile?.dlState : '',
                dlNumber: userType === USER_TYPE.CASH ? userProfile?.dlNumber : '',
                account:
                    userType === USER_TYPE.CREDIT
                        ? userAccount?.accountNumber || projectCookies?.CurrentWynneAccount
                        : 0,
                isDeliveryEstimate: !startDate && !endDate ? true : false
            };

            if (startDate && endDate) {
                const startDateTime = getStartDate(isFromHTGO);
                const endDateTime = getEndDate(isFromHTGO);
                if (startDateTime == endDateTime) {
                    dispatch({ type: 'endLoading' });
                    dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
                    dispatch({
                        type: SET_LOADERS_FIELD,
                        key: LOADERS.IS_ESTIMATES_LOADING,
                        value: false
                    });
                    return;
                }
            }
            if (
                startDate &&
                endDate &&
                (isValidString(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY)) > 0 ||
                    cart?.total_quantity > 0)
            ) {
                browsePCObj = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SOURCEPCOBJ)
                    ? JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.SOURCEPCOBJ))
                    : {};
            } else {
                browsePCObj = sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.LOCATIONPCOBJ)
                    ? JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.LOCATIONPCOBJ))
                    : {};
            }

            const formattedSelectedAccessories = formatSelectedAccessories(consumablesData?.updatedSelectedConsumables) || {};
            const cartEquipment = getEquipmentData(updatedItems) || [];
            const equipmentData = [...cartEquipment, ...Object.values(formattedSelectedAccessories)];         

            if (!localLocation) {
                const viewCartData =
                    (localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.VIEWCART) &&
                        JSON.parse(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.VIEWCART))) ||
                    {};
                const isCreditNewAddressFromStorage = localStorage.getItem(
                    STORAGE_CONFIG.LOCAL_STORAGE.ISCREDITNEWADDRESS
                )
                    ? JSON.parse(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ISCREDITNEWADDRESS))
                    : false;
                const overridePC = JSON.parse(
                    sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.OVERRIDEPC) || '{}'
                );
                if (userType === USER_TYPE.CREDIT && !isCreditNewAddressFromStorage && !isAlternateCID()) {
                    if (!projectDetails?.projectAddress1) {
                        filterDispatch({
                            type: RESET_GEOGRAPHY
                        });
                        dispatch({
                            type: RESET_ORDER_ESTIMATES
                        });

                        if (!isP2PUser) {
                            dispatch({ type: 'endLoading' });
                            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
                            dispatch({
                                type: SET_LOADERS_FIELD,
                                key: LOADERS.IS_ESTIMATES_LOADING,
                                value: false
                            });
                            return;
                        }
                    }
                    const pcLatVal =
                        String(overridePC?.latitude || '') ||
                        String(localPCLat || '') ||
                        String(browsePCObj?.lat || '') ||
                        String(viewCart?.pcLat || '') ||
                        String(viewCartData?.pcLat || '');
                    const pcLongVal =
                        String(overridePC?.longitude || '') ||
                        String(localPCLong || '') ||
                        String(browsePCObj?.long || '') ||
                        String(viewCart?.pcLong || '') ||
                        String(viewCartData?.pcLong || '');
                    // Whenever a store is changed by any user, we always update the selected store PC in ViewCart. So, using ViewCart.pc to get the correct selected store to fetch estimates.
                    payload = {
                        ...commonPayload,
                        pc: overridePC?.pc || browsePCObj?.pc || localPc || viewCart?.pc,
                        startDateAndTime: startDate ? getStartDate(isFromHTGO) : '',
                        endDateAndTime: endDate ? getEndDate(isFromHTGO) : '',
                        lat: String(projectDetails?.selectedProjectLatititude),
                        long: String(projectDetails?.selectedProjectLongitude),
                        equipment: equipmentData,
                        salesEquipments: getSalesEquipmentData(consumablesData?.updatedSelectedConsumables),
                        jobSiteAddr1: projectDetails?.projectAddress1,
                        jobSiteAddr2: projectDetails?.projectAddress2,
                        jobSiteCity: projectDetails?.selectedProjectCity,
                        jobSiteState: projectDetails?.selectedProjectState,
                        jobSiteZip: formatjobsiteZip(projectDetails?.selectedProjectZip),
                        pcLat: pcLatVal?.trim(),
                        pcLong: pcLongVal?.trim(),
                        projectId: String(projectDetails?.selectedProjectJobId || '') || projectCookies?.CurrentJobSite
                    };
                } else {
                    const pcLatVal =
                        String(overridePC?.latitude || '') || String(localPCLat || '') || String(browsePCObj?.lat || '') || String(viewCart?.pcLat || '');
                    const pcLongVal =
                        String(overridePC?.longitude || '') || String(localPCLong || '') || String(browsePCObj?.long || '') || String(viewCart?.pcLong || '');
                    payload = {
                        ...commonPayload,
                        pc: overridePC?.pc || browsePCObj?.pc || localPc || pc || 0,
                        startDateAndTime: startDate ? getStartDate(isFromHTGO) : '',
                        endDateAndTime: endDate ? getEndDate(isFromHTGO) : '',
                        lat: String(lat),
                        long: String(long),
                        equipment: equipmentData,
                        salesEquipments: getSalesEquipmentData(consumablesData?.updatedSelectedConsumables),
                        jobSiteAddr1: location,
                        jobSiteAddr2,
                        jobSiteCity,
                        jobSiteState,
                        jobSiteZip: formatjobsiteZip(jobSiteZip),
                        pcLat: pcLatVal?.trim(),
                        pcLong: pcLongVal?.trim(),
                        projectId: ''
                    };
                }
            } else {
                // case when payload is not coming from viewCart
                payload = {
                    ...commonPayload,
                    pc: localLocation?.pc,
                    startDateAndTime: startDate ? getStartDate(isFromHTGO) : '',
                    endDateAndTime: endDate ? getEndDate(isFromHTGO) : '',
                    lat: String(localLocation?.lat),
                    long: String(localLocation?.long),
                    equipment: equipmentData,
                    salesEquipments: getSalesEquipmentData(consumablesData?.updatedSelectedConsumables),
                    jobSiteAddr1: localLocation?.location,
                    jobSiteAddr2: localLocation?.jobSiteAddr2 || '',
                    jobSiteCity: localLocation?.jobSiteCity,
                    jobSiteState: localLocation?.jobSiteState,
                    jobSiteZip: formatjobsiteZip(localLocation?.jobSiteZip),
                    pcLat: String(localLocation?.pcLat || '')?.trim(),
                    pcLong: String(localLocation?.pcLong || '')?.trim(),
                    projectId: String(projectDetails?.selectedProjectJobId || '') || projectCookies?.CurrentJobSite
                };
            }
            const cartItems = updatedItems?.length || cart?.items?.length;
            if (cartItems) {
                if (
                    ((payload.startDateAndTime && payload.endDateAndTime) ||
                        (!payload.startDateAndTime && !payload.endDateAndTime)) &&
                    payload?.pc &&
                    payload?.pcLat &&
                    payload?.pcLong
                    // TODO 400 estimates fix with fetchRates
                    // && fetchRates(true)
                ) {
                    try {
                        dispatch({ type: 'beginLoading' });
                        if (signal) {
                            payload.signal = signal;
                        }
                        cartEstimates = await updateEstimates(cartResponse, cart, payload, dispatch)
                        if (cartEstimates) {
                            dispatch({ type: 'endLoading' });
                            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
                        }
                        if (!signal?.aborted) {
                            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.IS_ESTIMATES_LOADING, false);
                        }
                    } catch (err) {
                        logError(err, false, 'getEstimates', [localLocation, localPc, localPCLat, localPCLong]);
                    }
                } else {
                    dispatch({ type: 'endLoading' });
                    dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
                }
            }
    
            dispatch({ type: 'endLoading' });
            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
            if (!signal?.aborted) {
                dispatch({
                    type: SET_LOADERS_FIELD,
                    key: LOADERS.IS_ESTIMATES_LOADING,
                    value: false
                });
            }
            const rentalPriceObj = updateAccessoriesPrice(cartEstimates, formattedSelectedAccessories, consumablesData?.rentablesPriceObj);
            if (cartEstimates) {
                cartEstimates = { ...cartEstimates, rentalPriceObj };
            }
            return cartEstimates;
        } catch (error) {
            dispatch({
                type: SET_LOADERS_FIELD,
                key: LOADERS.IS_ESTIMATES_LOADING,
                value: false
            });
            logError(error, false, 'getEstimates');
        }
    };

    const api = {
        dispatch,
        getEstimates,
        getStartDate,
        getEndDate,
        isEstimatesWithOwnedPc,
        isSourcePCCallCriteria
    };
    return [api];
};

export default useCartEstimate;
